import type { ReactNode, Ref } from 'react'

import { MenuIcon } from '@/components/core/Icon/icons'
import { SIDEBAR_TEST_IDS } from '@/constants/testIds'

interface AppHeaderBarProps {
  /** Whatever the current page supplied via `<SetHeader>`. */
  children: ReactNode
  /**
   * Whether to render the drawer trigger. False above the mobile breakpoint, and
   * on routes that hide the sidebar (the roleplay flow) — there is no drawer to
   * open there.
   */
  showsDrawerTrigger: boolean
  isDrawerOpen: boolean
  onToggleDrawer: () => void
  /**
   * Forwarded to the bar element so the layout can measure it and publish
   * `--app-header-height`. The bar is one row of a fixed height per breakpoint
   * (`$banner-height` on desktop, `$mobile-header-height` on mobile — both pinned
   * in layouts/index.scss) and absent on pages that supply no header, so pages read
   * the measured value rather than re-deriving which of the three applies.
   */
  ref?: Ref<HTMLDivElement>
}

/**
 * The app's sticky top bar: the drawer trigger, then the current page's header
 * content.
 *
 * A component rather than inline markup in `AppLayout` because it owns a real
 * decision — whether the drawer trigger belongs here at all — and because the
 * layout should not also be in the business of describing a button. `AppLayout`
 * decides *whether* the bar renders; this decides what is in it.
 */
export function AppHeaderBar({ children, showsDrawerTrigger, isDrawerOpen, onToggleDrawer, ref }: AppHeaderBarProps) {
  return (
    <div ref={ref} className="app-layout__header">
      {showsDrawerTrigger && (
        <button
          type="button"
          className="app-layout__drawer-trigger"
          aria-label="Open navigation menu"
          aria-expanded={isDrawerOpen}
          data-testid={SIDEBAR_TEST_IDS.MOBILE_DRAWER_TRIGGER}
          onClick={onToggleDrawer}
        >
          <MenuIcon />
        </button>
      )}
      {children}
    </div>
  )
}
